import PropTypes from 'prop-types'
import DestinationSelector, { useDeliveryDestination } from './destination'
import { DELIVERY_POLICIES } from './policies'
import copy from './copy'
import styles from './styles.scss'

const countryNames = {
  th: ['Thailand', 'ประเทศไทย', 'Thailand'],
  sg: ['Singapore', 'สิงคโปร์', 'Singapura'],
  my: ['Malaysia', 'มาเลเซีย', 'Malaysia'],
  id: ['Indonesia', 'อินโดนีเซีย', 'Indonesia'],
  kh: ['Cambodia', 'กัมพูชา', 'Kamboja'],
  au: ['Australia', 'ออสเตรเลีย', 'Australia'],
  us: ['United States', 'สหรัฐอเมริกา', 'Amerika Serikat'],
  hk: ['Hong Kong', 'ฮ่องกง', 'Hong Kong'],
  mo: ['Macau', 'มาเก๊า', 'Makau'],
}

export const getPolicy = (destination, language) => {
  if (!destination || destination === 'global') return undefined
  const variants = DELIVERY_POLICIES[destination]
  if (!variants) return undefined
  return variants[language] || variants.en || variants.id
}

const PolicyIcon = ({ returns }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {returns ? (
      <path d="M9 4 4 9l5 5M4 9h9a6 6 0 0 1 0 12h-2" />
    ) : (
      <path d="M3 6h11v11H3V6Zm11 4h4l3 4v3h-7M6 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    )}
  </svg>
)
PolicyIcon.propTypes = { returns: PropTypes.bool }
PolicyIcon.defaultProps = { returns: false }

const PolicyRow = ({ title, lines, conditions, href, linkLabel, returns }) => (
  <details className="pdp-policy-row">
    <summary>
      <span className="pdp-policy-icon">
        <PolicyIcon returns={returns} />
      </span>
      <span className="pdp-policy-text">
        <strong>{title}</strong>
        {lines.filter(Boolean).map((line) => (
          <span key={line}>{line}</span>
        ))}
      </span>
      <span className="pdp-policy-chevron" aria-hidden="true">
        ⌄
      </span>
    </summary>
    <div className="pdp-policy-conditions">
      {!!conditions.length && (
        <ul>
          {conditions.map((condition) => (
            <li key={condition}>{condition}</li>
          ))}
        </ul>
      )}
      <a href={href}>{linkLabel}</a>
    </div>
  </details>
)
PolicyRow.propTypes = {
  title: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(PropTypes.string).isRequired,
  conditions: PropTypes.arrayOf(PropTypes.string).isRequired,
  href: PropTypes.string.isRequired,
  linkLabel: PropTypes.string.isRequired,
  returns: PropTypes.bool,
}
PolicyRow.defaultProps = { returns: false }

export const DeliveryPolicyContent = ({
  product,
  country,
  language,
  t,
  destination,
  selector,
}) => {
  const policy = getPolicy(destination.countrySlug, language)
  const languageIndex = { th: 1, id: 2 }[language.split('-')[0]] || 0
  const name =
    countryNames[destination.countrySlug]?.[languageIndex] ||
    destination.countryName
  const text = (key) => copy(t, key, language)
  const sourceCountry = destination.countrySlug || country
  let sourceLanguage = 'en'
  if (sourceCountry === 'id') sourceLanguage = 'id'
  if (sourceCountry === 'th' && language === 'th') sourceLanguage = 'th'
  const fallbackBase = `/${sourceCountry}/${sourceLanguage}/info/`
  const isPartner = product.is_italist === true
  const isPreorder = product.pre_order?.available === true
  const isComingSoon = product.is_coming_soon === true
  const eligible = product.is_returnable === true
  const excluded = product.is_returnable === false
  const exchange = destination.countrySlug === 'id'
  const returnsLinkLabel = exchange ? 'exchangePolicy' : 'returnPolicy'
  const standardDelivery = !isPartner && !isPreorder && !isComingSoon
  let deliveryLines = [
    policy?.shipping.timing
      ? `${text('estimate')}: ${policy.shipping.timing}`
      : text(name ? 'timingUnknown' : 'deliveryUnknown'),
    policy?.shipping.fees || text('fees'),
  ]
  if (isPartner) deliveryLines = [text('partner')]
  else if (isPreorder) deliveryLines = [text('preorder')]
  else if (isComingSoon) deliveryLines = [text('comingSoon')]

  let returnLines = [text('eligibility')]
  if (excluded) returnLines = [text('nonReturnable')]
  else if (isPartner) returnLines = [text('partner')]
  else if (eligible && policy)
    returnLines = [policy.returns.summary, policy.returns.method].filter(
      Boolean,
    )

  return (
    <section
      className="pdp-delivery-policy"
      aria-label={`${text('heading')} ${name || text('destination')}`}
      data-cy="pdp-delivery-policy"
    >
      <style jsx>{styles}</style>
      <div className="pdp-policy-heading">
        <strong>
          {name ? `${text('heading')} ${name}` : text('destination')}
        </strong>
        {selector}
      </div>
      <PolicyRow
        title={
          standardDelivery && policy?.shipping.summary
            ? policy.shipping.summary
            : text('delivery')
        }
        lines={deliveryLines}
        conditions={standardDelivery ? policy?.shipping.conditions || [] : []}
        href={
          isPartner
            ? `${fallbackBase}italist`
            : policy?.sources.shipping || `${fallbackBase}shipping`
        }
        linkLabel={text(isPartner ? 'partnerPolicy' : 'shippingPolicy')}
      />
      <PolicyRow
        title={text(exchange ? 'exchanges' : 'returns')}
        lines={returnLines}
        conditions={
          !isPartner && eligible ? policy?.returns.conditions || [] : []
        }
        href={
          isPartner
            ? `${fallbackBase}italist`
            : policy?.sources.returns || `${fallbackBase}returns`
        }
        linkLabel={text(isPartner ? 'partnerPolicy' : returnsLinkLabel)}
        returns
      />
    </section>
  )
}

const productType = PropTypes.shape({
  is_italist: PropTypes.bool,
  is_returnable: PropTypes.bool,
  is_coming_soon: PropTypes.bool,
  pre_order: PropTypes.shape({ available: PropTypes.bool }),
})

DeliveryPolicyContent.propTypes = {
  product: productType.isRequired,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  destination: PropTypes.shape({
    countrySlug: PropTypes.string,
    countryName: PropTypes.string,
  }).isRequired,
  selector: PropTypes.node,
}
DeliveryPolicyContent.defaultProps = { selector: null }

const DeliveryPolicy = ({ product, country, language, t }) => {
  const destination = useDeliveryDestination(country)
  return (
    <DeliveryPolicyContent
      product={product}
      country={country}
      language={language}
      t={t}
      destination={destination}
      selector={
        <DestinationSelector
          shop={country}
          label={copy(t, 'change', language)}
          marketLabel={copy(t, 'market', language)}
        />
      }
    />
  )
}
DeliveryPolicy.propTypes = {
  product: productType.isRequired,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}
export default DeliveryPolicy
